import { inject } from "mobx-react";
import React from "react";
import { useTranslation } from "react-i18next";
import { DatePicker, Input, MultiSelect, Option } from "src/components";
import { theme } from "src/config";
import { LogStore } from "src/stores";
import { ItemStatus, LogActions, RoomLogProps } from "src/types";
import { debounce } from "src/util/util";
import styled from "styled-components";

export interface ILogFilterProps {
  prop:      RoomLogProps,
  type:      "select" | "text" | "number" | "date",
  logStore?: LogStore,
}

export const LogFilter = (props: ILogFilterProps) => {
  switch(props.type) {
    case "select":
      return <SelectFilter {...props} />
    case "text":
      return <TextFilter {...props} />
    case "number":
      return <NumberFilter {...props} />
    case "date":
      return <DateFilter {...props} />
    default:
      console.error("Incorrect filter type");
      return null;
  }
}

export const SelectFilter = inject("logStore")((props: ILogFilterProps) => {
  const { t, i18n } = useTranslation();
  const [selection, setSelection] = React.useState<Option[]>([]);

  const options = props.prop === "status" ?
    [ItemStatus.Critical, ItemStatus.Answered, ItemStatus.Active]
    : Object.values(LogActions);
  
  return (
    <StyledMultiSelect 
      singleSelect
      disableSearch
      options  = {options.map(s => ({label: t(s), value: s}))}
      selected = {selection}
      onChange = {(s) => {
        setSelection([s]);
        props.logStore.setRoomLogFilter(props.prop, s?.value || null);
      }}
    />
  );
});

export const TextFilter = inject("logStore")((props: ILogFilterProps) => {
  const { t, i18n } = useTranslation();
  const { logStore, prop, type } = props;
  const [text, setText] = React.useState<string>(logStore.roomLogFilter[prop] || "");
  const debouncedChange = React.useCallback(
    debounce((t: string) => logStore.setRoomLogFilter(prop, t || null))
  , []);
  React.useEffect(() => {
    debouncedChange(text);
  }, [text]);

  return (
    <StyledTextInput 
      style       = {{marginLeft: "0.9rem"}}
      placeholder = {t("filter")}
      value       = {text}
      onChange    = {setText}
    />
  );
});

export const DateFilter = inject("logStore")((props: ILogFilterProps) => {
  const [start, setStart] = React.useState<Date>();
  const [end,   setEnd]   = React.useState<Date>(new Date());

  return (
    <DatePickerContainer>
    <DatePicker 
      end           = {end}
      start         = {start}
      onRangeChange = {(s, e) => {
        setStart(s); 
        setEnd(e);
        props.logStore.setRoomLogFilter(props.prop, {start: s, end: e});
      }}
      variant       = "underlined"
      range
    />
    </DatePickerContainer>
  );
});

export const NumberFilter = inject("logStore")((props: ILogFilterProps) => {
  const { t, i18n } = useTranslation();
  const { logStore, prop, type } = props;
  const [text, setText] = React.useState<string>( logStore.roomLogFilter[prop] ? ""+logStore.roomLogFilter[prop][0] : "");

  const debouncedChange = React.useCallback(
    debounce((t: string) => props.logStore.setRoomLogFilter(props.prop, t === "" ? null : Number(t)))
  , []);
  React.useEffect(() => {
    debouncedChange(text);
  }, [text]);

  return (
    <StyledTextInput 
      placeholder = {"#"}
      value       = {text}
      onChange    = {setText}
      type        = "number"
      min         = "0"
      style       = {{maxWidth: "5rem", marginLeft: "0.9rem"}}
    />
  );
});

const StyledTextInput = styled(Input)`
  border:        0;
  border-bottom: 1px solid;
  border-radius: 0;
  border-color:  ${theme.color.input.border};
  padding:       0.5rem;
  padding-left:  1rem;
  max-width:     10rem;
`;

const DatePickerContainer = styled.div`
  margin-left: 1rem;
`

const StyledMultiSelect = styled(MultiSelect)`
  padding-bottom: 0.5rem;
`